import { colors, radius, transitions, shadows } from './tokens';

export const components = {
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                backgroundColor: colors.surface.base,
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
            },
            '*::selection': {
                backgroundColor: colors.primary[500],
                color: '#ffffff',
            },
            '*::-webkit-scrollbar': { width: 10, height: 10 },
            '*::-webkit-scrollbar-track': { background: 'transparent' },
            '*::-webkit-scrollbar-thumb': {
                background: colors.border.default,
                borderRadius: 6,
                border: '2px solid transparent',
                backgroundClip: 'padding-box',
            },
            '*::-webkit-scrollbar-thumb:hover': {
                background: colors.border.strong,
                backgroundClip: 'padding-box',
                border: '2px solid transparent',
            },
        },
    },

    MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
            root: {
                borderRadius: radius.md,
                padding: '8px 16px',
                fontWeight: 500,
                textTransform: 'none',
                transition: `all ${transitions.base}`,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
            },
            contained: {
                backgroundColor: colors.primary[500],
                '&:hover': { backgroundColor: colors.primary[600] },
            },
            outlined: {
                borderColor: colors.border.default,
                color: colors.text.primary,
                '&:hover': {
                    borderColor: colors.border.strong,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                },
            },
            text: {
                color: colors.text.secondary,
                '&:hover': {
                    color: colors.text.primary,
                    backgroundColor: 'rgba(255,255,255,0.04)',
                },
            },
        },
    },

    MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
            root: {
                backgroundImage: 'none',
                backgroundColor: colors.surface.raised,
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: radius.lg,
                // Subtle top-edge inner highlight — "lit from above" Linear trick
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
            },
        },
    },

    MuiAppBar: {
        styleOverrides: {
            root: {
                borderRadius: 0,
                border: 'none',
                borderBottom: `1px solid ${colors.border.subtle}`,
                boxShadow: 'none',
            },
        },
    },

    MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
            root: {
                backgroundColor: colors.surface.raised,
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: radius.lg,
                backgroundImage: 'none',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
            },
        },
    },

    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: radius.sm,
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 500,
            },
        },
    },

    MuiDivider: {
        styleOverrides: {
            root: { borderColor: colors.border.subtle },
        },
    },

    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: radius.md,
                backgroundColor: colors.surface.subtle,
                transition: `box-shadow ${transitions.fast}, border-color ${transitions.fast}`,
                '& fieldset': { borderColor: colors.border.default, transition: `border-color ${transitions.fast}` },
                '&:hover fieldset': { borderColor: colors.border.strong },
                '&.Mui-focused': {
                    boxShadow: shadows.focus,
                    '& fieldset': { borderColor: colors.primary[500] },
                },
            },
            input: {
                '&::placeholder': { color: colors.text.muted, opacity: 1 },
            },
        },
    },

    MuiInputLabel: {
        styleOverrides: {
            root: {
                color: colors.text.secondary,
                '&.Mui-focused': { color: colors.primary[400] },
            },
        },
    },

    MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
    },

    MuiSelect: {
        styleOverrides: {
            icon: { color: colors.text.muted },
        },
    },

    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                backgroundColor: colors.surface.elevated,
                border: `1px solid ${colors.border.default}`,
                color: colors.text.primary,
                fontSize: '0.75rem',
                padding: '6px 10px',
                borderRadius: radius.sm,
                boxShadow: shadows.md,
            },
            arrow: { color: colors.surface.elevated },
        },
    },

    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: radius.md,
                border: `1px solid ${colors.border.subtle}`,
                fontSize: '0.875rem',
            },
            standardInfo: {
                backgroundColor: 'rgba(99,102,241,0.08)',
                borderColor: 'rgba(99,102,241,0.2)',
                color: colors.text.primary,
            },
            standardSuccess: {
                backgroundColor: colors.performance.positiveBg,
                borderColor: colors.performance.positiveBorder,
                color: colors.text.primary,
            },
            standardError: {
                backgroundColor: colors.performance.negativeBg,
                borderColor: colors.performance.negativeBorder,
                color: colors.text.primary,
            },
        },
    },

    MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
            root: {
                color: colors.text.link,
                transition: `color ${transitions.fast}`,
                '&:hover': { color: colors.primary[300] },
            },
        },
    },

    MuiMenu: {
        styleOverrides: {
            paper: {
                backgroundColor: colors.surface.elevated,
                border: `1px solid ${colors.border.default}`,
                boxShadow: shadows.lg,
            },
        },
    },

    MuiMenuItem: {
        styleOverrides: {
            root: {
                fontSize: '0.875rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
            },
        },
    },

    MuiSwitch: {
        styleOverrides: {
            switchBase: {
                '&.Mui-checked': {
                    color: colors.primary[400],
                    '& + .MuiSwitch-track': { backgroundColor: colors.primary[500] },
                },
            },
            track: { backgroundColor: colors.border.strong },
        },
    },

    MuiAvatar: {
        styleOverrides: {
            root: {
                backgroundColor: colors.surface.elevated,
                border: `1px solid ${colors.border.subtle}`,
            },
        },
    },

    MuiSkeleton: {
        styleOverrides: {
            root: { backgroundColor: 'rgba(255,255,255,0.05)' },
        },
    },

    MuiCircularProgress: {
        styleOverrides: {
            root: { color: colors.primary[400] },
        },
    },
};
